import { useState } from "react";
import { Button } from "@/components/Base/Button";
import { CardTagsForm } from "@/components/Dashboard/components/Card/CardTagsForm";
import { RenderTag } from "@/components/Dashboard/components/Card/Card.Tags";
import { buildTagEditPatch } from "@/lib/cards";
import { sortTagsByShopOrder } from "@/lib/tags";
import { card as cardRepository } from "@/repositories/cards";
import { TagsRepository } from "@/repositories/tags";
import { useStore } from "@/stores/useStore";
import { Card } from "@/types/v2";

export interface TagsPanelProps {
  cardData: Card;
  shopId: string;
  /** Read-only card: show the tags as chips instead of the editor. */
  disabled?: boolean;
}

/**
 * Body of the activity popover's Tags tab. Saving does exactly what the RO
 * details panel's tag editor does (catalog first, then the card); the popover
 * stays open and the card re-renders live from the store.
 */
export const TagsPanel = ({ cardData, shopId, disabled }: TagsPanelProps) => {
  // Same source Card.Menu uses, so no prop plumbing through Card/CardFooter.
  const catalog = useStore((s) => s.tags);
  const [saving, setSaving] = useState(false);

  if (disabled) {
    const tags = sortTagsByShopOrder(cardData.tags ?? [], catalog?.tags);
    if (tags.length === 0) {
      return <p className="text-sm text-muted-foreground">No tags</p>;
    }
    return (
      <div className="flex flex-row flex-wrap gap-0.5">
        {tags.map((label, index) => (
          <RenderTag
            key={`${label}-${index}`}
            label={label}
            tag={catalog?.tags?.find(
              (t) => t.label.toLowerCase() === label.toLowerCase()
            )}
          />
        ))}
      </div>
    );
  }

  const handleSubmit = async (values: { tags: string[] }) => {
    setSaving(true);
    try {
      await TagsRepository.addTag(shopId, values.tags, catalog);
      await cardRepository.update(
        shopId,
        cardData.id,
        buildTagEditPatch(cardData, values)
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CardTagsForm
      cardData={cardData}
      onSubmit={handleSubmit}
      submitButton={
        <Button type="submit" variant="primary-alt" disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      }
    />
  );
};
